import { AccountType } from '@/data/usecases/account/account-type'
import { ContractedPlanLicense } from '@/data/usecases/plan-license/contracted-plan-license'
import { Account } from '@/domain/usecases'
import { AccountBenefit } from '@/domain/usecases/account/account-benefit'
import { AccountProfessional } from '@/domain/usecases/account/account-professional'
import { CompanyManagerAccount } from '@/domain/usecases/account/company-manager/company-manager-account-profile'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { QueryClient } from '@tanstack/react-query'
import {
  UserProfileLoaderBenefitOptionsType,
  UserProfileLoaderDataType,
  UserProfileLoaderProfessionalOptionsType
} from '@/main/routes/data-loaders/user/user-profile'
import { queryClient } from '@/infra/http'

export type CompanyManagerUserProfileLoaderDataType = UserProfileLoaderDataType & {
  accountStatus: CompanyManagerAccount.StatusModel
  accountInactivationReasons: CompanyManagerAccount.InactivationReasonListModel
  ownProfile: boolean
}

/**
 * Loads user profile data for editing as company manager
 * Can edit collaborator or dependent profiles
 */
export async function loadCompanyManagerUserProfile(
  accountId: string,
  currentAccountId: string
): Promise<CompanyManagerUserProfileLoaderDataType> {
  const { accountProfile, accountStatus, accountInactivationReasons } = await getAccountProfileData(
    accountId,
    queryClient
  )

  const [accountProfessional, { accountBenefits, benefitsOptions }, professionalOptions] =
    await Promise.all([
      getAccountProfessional(accountProfile, queryClient),
      getAccountBenefits(accountProfile, queryClient),
      getProfessionalOptions(accountProfile, queryClient)
    ])

  return {
    accountProfile,
    accountStatus,
    accountInactivationReasons,
    accountProfessional,
    accountBenefits,
    benefitsOptions,
    professionalOptions,
    ownProfile: accountId === currentAccountId
  }
}

async function getAccountProfileData(accountId: string, queryClient: QueryClient) {
  const module = await import(
    '@remote/account/company-manager/remote-company-manager-account-profile-factory'
  )
  const makeRemote = module.makeRemoteCompanyManagerAccountProfile
  const remote = makeRemote(accountId, queryClient)

  const [accountProfile, accountStatus, accountInactivationReasons] = await Promise.all([
    remote.profile(),
    remote.getStatus(),
    remote.getInactivationReasons()
  ])

  return { accountProfile, accountStatus, accountInactivationReasons }
}

async function getProfessionalOptions(
  accountProfile: Account.ProfileModel,
  queryClient: QueryClient
): Promise<UserProfileLoaderProfessionalOptionsType | undefined> {
  if (AccountType.Enum.DEPENDENT.is(accountProfile)) return undefined

  const module = await import('@/main/factories/usecases')
  const makeRemoteOnboardingOptions = module.makeRemoteOnboardingOptions as (
    queryClient: QueryClient
  ) => OnboardingOptions
  const remoteOnboardingOptions = makeRemoteOnboardingOptions(queryClient)
  const data = await remoteOnboardingOptions.getStep1()

  return {
    areas: data.professionalAreas,
    seniorities: data.professionalSeniorities
  }
}

async function getAccountProfessional(
  accountProfile: Account.ProfileModel,
  queryClient: QueryClient
): Promise<AccountProfessional.Model | undefined> {
  if (AccountType.Enum.DEPENDENT.is(accountProfile)) return undefined

  const module = await import('@/main/factories/usecases')
  const makeRemote = module.makeRemoteAccountProfessional as (
    queryClient: QueryClient
  ) => AccountProfessional
  const remote = makeRemote(queryClient)
  return remote.get(accountProfile.id)
}

async function getBenefitsOptions(
  accountProfile: Account.ProfileModel,
  accountBenefits: AccountBenefit.Model,
  queryClient: QueryClient
): Promise<AccountBenefit.OptionsModel> {
  const holderId = accountBenefits.contractedPlan.holder?.id
  if (holderId) {
    return await getDependentBenefitsOptions(holderId, queryClient)
  }
  return await getCollaboratorBenefitsOptions(accountProfile, queryClient)
}

async function getCollaboratorBenefitsOptions(
  accountProfile: Account.ProfileModel,
  queryClient: QueryClient
): Promise<AccountBenefit.OptionsModel> {
  const module = await import(
    '@remote/account/company-manager/remote-company-manager-plan-license-factory'
  )
  const makeRemotePlanLicense = module.makeRemoteCompanyPlanLicense
  const remote = makeRemotePlanLicense(accountProfile.company.uniqueId, queryClient)
  const licenses = await remote.loader()

  return {
    passPlans: licenses
      .filter(license => accountProfile.accountType === license.accountType)
      .map(license => ({
        id: license.id,
        name: license.name,
        contracted: !!license.available
      })) as ContractedPlanLicense.ListModel
  }
}

async function getDependentBenefitsOptions(
  accountId: string,
  queryClient: QueryClient
): Promise<AccountBenefit.OptionsModel> {
  const module = await import(
    '@remote/account/company-manager/remote-company-manager-dependent-registration-options-factory'
  )
  const makeRemote = module.makeRemoteCompanyManagerDependentRegistrationOptions
  const remote = makeRemote(accountId, queryClient)
  return remote.loader()
}

async function getAccountBenefits(
  accountProfile: Account.ProfileModel,
  queryClient: QueryClient
): Promise<UserProfileLoaderBenefitOptionsType> {
  const module = await import('@/main/factories/usecases')
  const makeRemote = module.makeRemoteAccountProfile as (queryClient: QueryClient) => Account
  const remoteBenefits = makeRemote(queryClient)
  const accountBenefits = await remoteBenefits.getBenefits(accountProfile.id)

  return {
    accountBenefits,
    benefitsOptions: await getBenefitsOptions(accountProfile, accountBenefits, queryClient)
  }
}
